import { FC } from 'react'

import _ from 'lodash'
import { FormProvider, useForm } from 'react-hook-form'

import { LoadingContainer } from 'peach/components'
import { StyledButton } from 'peach/components/Button/Button'
import Input from 'peach/components/Input'
import SelectInput from 'peach/components/SelectInput'

import { trueFalseSelectOptions } from 'core/constants'
import { R } from 'core/helpers'
import variables from 'core/theme/variables'
import { useCreateCompany, type Company } from 'core/types'

import {
  ErrorComponent,
  FormActions,
  Section,
  SectionLabel,
  Title,
} from './Components'
import { Company as CreateCompanyWizardCompany } from './types'
import {
  brandLogoFieldConfig,
  cleanValues,
  getBrandLogos,
  stripBrandLogosWhenSlugBlank,
} from './utils'

type FormType = Partial<Company>

type CreateCompanyProps = {
  updateCompany: (obj: CreateCompanyWizardCompany) => void
  title: string
}

const CreateCompany: FC<CreateCompanyProps> = ({ updateCompany, title }) => {
  const {
    mutateAsync: createCompany,
    isPending,
    isError,
    error,
  } = useCreateCompany({})

  const form = useForm<FormType>({
    defaultValues: {
      legalName: '',
      slug: '',
      domainName: '',
      shortDomainName: '',
      name: '',
      brandName: '',
      borrowerAuthType: 'oneTimeCodeEmail',
      agentAuthType: 'oneTimeCodeEmail',
      borrowerAuthValueType: 'email',
      agentAuthValueType: 'email',
      tokenizationKeyId: 567,
      status: 'active',
      config: {
        links: {
          helpUrl: '',
          termsOfUseUrl: '',
          privacyPolicyUrl: '',
        },
        payment: {
          processedByPeach: true,
          transactionLimitAmount: 100000,
        },
        support: {
          email: '',
          phone: '+18885551234',
          departmentName: 'Support',
          mailingAddress: {
            city: 'Montreal',
            state: 'QC',
            postalCode: 'H2Y 2W2',
            addressLine1: `500 Place d'Armes`,
            addressLine2: 'Suite 1800',
          },
        },
        timezone: 'America/New_York',
        openHours: {
          businessHours: {
            normal: 'Mon-Fri 10:00-18:00',
          },
        },
        statements: {
          isMailStatementAllowed: false,
        },
        brandAssets: {
          colors: {
            primary: variables.colorBluePrimary,
            tertiary: '#009FFF',
            secondary: '#59CD90',
          },
          copies: {
            consent: '',
            disclosures: '',
          },
        },
        collections: {
          intensityLimits: {
            7: {
              heavy: 5,
              light: 1,
              normal: 3,
            },
            30: {
              heavy: 15,
              light: 3,
              normal: 8,
            },
          },
        },
        paymentTypes: {
          ach: true,
          check: false,
          debitCard: false,
          creditCard: false,
          balanceCheck: {
            plaid: true,
          },
          achVerification: {
            plaid: true,
            microdeposit: true,
          },
        },
        communication: {
          email_from: {
            name: '',
            email: '',
          },
          address_from: {
            city: 'Montreal',
            state: 'QC',
            postalCode: 'H2Y 2W2',
            addressLine1: `500 Place d'Armes`,
            addressLine2: 'Suite 1800',
          },
          text_from_number: '+18005551234',
          enableInboundWebChat: true,
          enableInboundVoiceCall: true,
          enable_two_way_texting: true,
        },
        requireBorrowerIdentity: true,
      },
    },
  })

  const syncBrandLogosFromSlug = (slug: string) => {
    form.setValue(
      'config.brandAssets.logos',
      slug ? getBrandLogos(slug) : undefined,
    )
  }

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const resp = (await createCompany(
        cleanValues(stripBrandLogosWhenSlugBlank(values)) as FormType,
      )) as { data: Company & { id: string } }

      if (resp?.data?.id) {
        updateCompany({
          companyId: resp?.data.id,
          legalName: resp?.data?.legalName ?? undefined,
          name: resp?.data?.name ?? undefined,
          brandName: resp?.data?.brandName ?? undefined,
          completed: false,
        })
      }
    } catch (e) {
      console.error('!!e', e)
    }
  })

  const authTypeOptions = [
    { value: 'basic', label: 'Basic' },
    { value: 'google', label: 'Google' },
    { value: 'oneTimeCodeEmail', label: 'One Time Code Email' },
    { value: 'oneTimeCodeText', label: 'One Time Code Text' },
    { value: 'SAML', label: 'SAML' },
  ]

  const authTypeValueOptions = [
    { value: 'email', label: 'Email' },
    { value: 'username', label: 'Username' },
    { value: 'phone', label: 'Phone' },
    { value: 'samlSubjectNameId', label: 'SAML Subject Name ID' },
  ]

  return (
    <LoadingContainer loading={isPending}>
      <Title>{title}</Title>
      <form onSubmit={onSubmit}>
        <FormProvider {...form}>
          <Input
            label='Legal Name'
            placeholder='Company, Inc.'
            required
            type='text'
            {...form.register('legalName', {
              onChange: (e) => {
                form.setValue('legalName', e.target.value)
                const slug = _.kebabCase(e.target.value)
                form.setValue('slug', slug)
                syncBrandLogosFromSlug(slug)
              },
            })}
          />
          <Input
            label='Slug'
            placeholder='company-inc'
            type='text'
            {...form.register('slug', {
              onChange: (e) => {
                const slug = e.target.value
                form.setValue('slug', slug)
                syncBrandLogosFromSlug(slug)
              },
            })}
          />

          <Section>
            <SectionLabel>Brand assets — logos</SectionLabel>
            {R.map(brandLogoFieldConfig, ({ key, label }) => (
              <Input
                key={key}
                label={label}
                placeholder='https://...'
                type='text'
                {...form.register(`config.brandAssets.logos.${key}`)}
              />
            ))}
          </Section>

          <Input label='Name' type='text' {...form.register('name')} />
          <Input
            label='Brand Name'
            type='text'
            {...form.register('brandName')}
          />

          <Input
            label='Domain Name'
            type='text'
            {...form.register('domainName')}
          />
          <Input
            label='Short Domain Name'
            placeholder='c.peach.to'
            type='text'
            {...form.register('shortDomainName')}
          />
          {/* <Input type='text' label='Type' {...form.register('type')} /> */}

          <SelectInput
            control={form.control}
            label='Borrower Auth Type'
            name='borrowerAuthType'
            options={authTypeOptions as any}
            placeholder='Select a borrower auth type'
          />

          <SelectInput
            control={form.control}
            label='Agent Auth Type'
            name='agentAuthType'
            options={authTypeOptions as any}
            placeholder='Select an agent auth type'
          />

          <SelectInput
            control={form.control}
            label='Borrower Auth Value Type'
            name='borrowerAuthValueType'
            options={authTypeValueOptions as any}
            placeholder='Select a borrower auth type value'
          />

          <SelectInput
            control={form.control}
            label='Agent Auth Value Type'
            name='agentAuthValueType'
            options={authTypeValueOptions as any}
            placeholder='Select an agent auth type value'
          />

          <Input
            label='Tokenization Key ID'
            type='number'
            {...form.register('tokenizationKeyId', { valueAsNumber: true })}
          />

          <SelectInput
            control={form.control}
            label='Status'
            name='status'
            options={[
              { value: 'active', label: 'Active' },
              { value: 'activeChurning', label: 'Active Churning' },
              { value: 'inactive', label: 'Inactive' },
            ]}
            placeholder='Select a status'
          />

          <Section>
            <SectionLabel>Config</SectionLabel>

            <Section>
              <SectionLabel>Borrower Verification</SectionLabel>
              <Input
                label='CRM Default Type Id'
                type='text'
                {...form.register(
                  'config.borrowerVerification.crmDefaultTypeId',
                )}
              />
            </Section>

            <Section>
              <SectionLabel>Communication</SectionLabel>

              <Section>
                <SectionLabel>Email From</SectionLabel>
                <Input
                  label='Name'
                  placeholder='Company Customer Service'
                  type='text'
                  {...form.register('config.communication.email_from.name')}
                />
                <Input
                  label='Email'
                  placeholder='support@company-borrower.peach-sandbox.finance'
                  type='text'
                  {...form.register('config.communication.email_from.email')}
                />
              </Section>

              <Section>
                <SectionLabel>Address From</SectionLabel>
                <Input
                  label='City'
                  type='text'
                  {...form.register('config.communication.address_from.city')}
                />
                <Input
                  label='State'
                  type='text'
                  {...form.register('config.communication.address_from.state')}
                />
                <Input
                  label='Postal Code'
                  type='text'
                  {...form.register(
                    'config.communication.address_from.postalCode',
                  )}
                />
                <Input
                  label='Address Line 1'
                  type='text'
                  {...form.register(
                    'config.communication.address_from.addressLine1',
                  )}
                />
                <Input
                  label='Address Line 2'
                  type='text'
                  {...form.register(
                    'config.communication.address_from.addressLine2',
                  )}
                />
              </Section>
              <Input
                label='Text From Number'
                type='text'
                {...form.register('config.communication.text_from_number')}
              />

              <SelectInput
                control={form.control}
                label='Enable Inbound Web Chat'
                name='config.communication.enableInboundWebChat'
                options={trueFalseSelectOptions}
                placeholder='Select true/false'
              />
              <SelectInput
                control={form.control}
                label='Enable Inbound Voice Call'
                name='config.communication.enableInboundVoiceCall'
                options={trueFalseSelectOptions}
                placeholder='Select true/false'
              />
              <SelectInput
                control={form.control}
                label='Enable Two-Way Texting'
                name='config.communication.enable_two_way_texting'
                options={trueFalseSelectOptions}
                placeholder='Select true/false'
              />
            </Section>

            <Section>
              <SectionLabel>Support</SectionLabel>
              <Input
                label='Phone'
                type='text'
                {...form.register('config.support.phone')}
              />
              <Input
                label='Email'
                placeholder='support@company-borrower.peach-sandbox.finance'
                type='text'
                {...form.register('config.support.email')}
              />
              <Input
                label='Department Name'
                type='text'
                {...form.register('config.support.departmentName')}
              />

              <Section>
                <SectionLabel>Mailing Address</SectionLabel>
                <Input
                  label='City'
                  type='text'
                  {...form.register('config.support.mailingAddress.city')}
                />
                <Input
                  label='State'
                  type='text'
                  {...form.register('config.support.mailingAddress.state')}
                />
                <Input
                  label='Postal Code'
                  type='text'
                  {...form.register('config.support.mailingAddress.postalCode')}
                />
                <Input
                  label='Address Line 1'
                  type='text'
                  {...form.register(
                    'config.support.mailingAddress.addressLine1',
                  )}
                />
                <Input
                  label='Address Line 2'
                  type='text'
                  {...form.register(
                    'config.support.mailingAddress.addressLine2',
                  )}
                />
              </Section>
            </Section>

            <Section>
              <SectionLabel>Payment</SectionLabel>
              <SelectInput
                control={form.control}
                label='Processed By Peach'
                name='config.payment.processedByPeach'
                options={trueFalseSelectOptions}
                placeholder='Select true/false'
              />
              <Input
                label='Transaction Limit Amount'
                type='number'
                {...form.register('config.payment.transactionLimitAmount', {
                  valueAsNumber: true,
                })}
              />
            </Section>

            <Section>
              <SectionLabel>Payment Types</SectionLabel>
              <SelectInput
                control={form.control}
                label='ACH'
                name='config.paymentTypes.ach'
                options={trueFalseSelectOptions}
                placeholder='Select true/false'
              />
              <SelectInput
                control={form.control}
                label='Check'
                name='config.paymentTypes.check'
                options={trueFalseSelectOptions}
                placeholder='Select true/false'
              />
              <SelectInput
                control={form.control}
                label='Debit Card'
                name='config.paymentTypes.debitCard'
                options={trueFalseSelectOptions}
                placeholder='Select true/false'
              />
              <SelectInput
                control={form.control}
                label='Credit Card'
                name='config.paymentTypes.creditCard'
                options={trueFalseSelectOptions}
                placeholder='Select true/false'
              />

              <Section>
                <SectionLabel>Balance Check</SectionLabel>
                <SelectInput
                  control={form.control}
                  label='Plaid'
                  name='config.paymentTypes.balanceCheck.plaid'
                  options={trueFalseSelectOptions}
                  placeholder='Select true/false'
                />
              </Section>

              <Section>
                <SectionLabel>ACH Verification</SectionLabel>
                <SelectInput
                  control={form.control}
                  label='Plaid'
                  name='config.paymentTypes.achVerification.plaid'
                  options={trueFalseSelectOptions}
                  placeholder='Select true/false'
                />
                <SelectInput
                  control={form.control}
                  label='Microdeposits'
                  name='config.paymentTypes.achVerification.microdeposit'
                  options={trueFalseSelectOptions}
                  placeholder='Select true/false'
                />
              </Section>
            </Section>

            <Input
              label='Timezone'
              type='text'
              {...form.register('config.timezone')}
            />

            <Section>
              <SectionLabel>Open Hours</SectionLabel>

              <Section>
                <SectionLabel>Business Hours</SectionLabel>
                <Input
                  label='Normal'
                  type='text'
                  {...form.register('config.openHours.businessHours.normal')}
                />
              </Section>
            </Section>

            <Section>
              <SectionLabel>UI</SectionLabel>
              <Input
                label='Favicon'
                placeholder='https://...'
                type='text'
                {...form.register('config.ui.favicon')}
              />
            </Section>
          </Section>

          {isError &&
            !!error &&
            typeof error === 'object' &&
            'message' in error &&
            typeof error.message === 'string' && (
              <ErrorComponent>{error.message}</ErrorComponent>
            )}
          <FormActions>
            <StyledButton $primary type='submit'>
              Submit
            </StyledButton>
          </FormActions>
        </FormProvider>
      </form>
    </LoadingContainer>
  )
}

export default CreateCompany
